import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { performHardLogout, useAuthStore, useSettingsStore } from '../store';
import {
  LogOut,
  BookOpen,
  History,
  Settings,
  Library,
  Database,
  Video,
  CloudOff,
  Menu,
  X,
  BookMarked,
  ClipboardCheck,
} from 'lucide-react';
import { canAccessLearnerArea, canAccessMediaManagement, canAccessQuestionApproval, canAccessQuestionManagement, canAccessStudentManagement, canAccessVocabManagement, getDefaultAuthenticatedRoute, normalizeUserRole } from '../utils/permissions';

interface LayoutMobileProps {
  pendingCount: number;
}

const LayoutMobile: React.FC<LayoutMobileProps> = ({ pendingCount }) => {
  const { user } = useAuthStore();
  const { systemLanguage } = useSettingsStore();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const canAccessAdmin = canAccessStudentManagement(user?.role);
  const homeHref = getDefaultAuthenticatedRoute(user?.role);
  const text = systemLanguage === 'en'
    ? {
        home: 'Home',
        history: 'History',
        stats: 'Stats',
        admin: 'Admin',
        adminPanel: 'Admin Panel',
        questions: 'Questions',
        approvals: 'Approvals',
        media: 'Media',
        vocab: 'Vocab',
        student: 'Student',
        openMenu: 'Open menu',
        closeMenu: 'Close menu',
        adminSection: 'Admin',
        logout: 'Log out',
      }
    : systemLanguage === 'id'
      ? {
          home: 'Beranda',
          history: 'Riwayat',
          stats: 'Statistik',
          admin: 'Admin',
          adminPanel: 'Panel Admin',
          questions: 'Soal',
          approvals: 'Persetujuan',
          media: 'Media',
          vocab: 'Kosakata',
          student: 'Siswa',
          openMenu: 'Buka menu',
          closeMenu: 'Tutup menu',
          adminSection: 'Admin',
          logout: 'Keluar',
        }
      : {
          home: '首页',
          history: '历史',
          stats: '统计',
          admin: '管理',
          adminPanel: '管理面板',
          questions: '题目',
          approvals: '审批',
          media: '媒体',
          vocab: '词汇',
          student: '学生',
          openMenu: '打开菜单',
          closeMenu: '关闭菜单',
          adminSection: '管理',
          logout: '退出登录',
        };

  const primaryNav = canAccessLearnerArea(user?.role)
    ? [
        { name: text.home, href: '/', icon: BookOpen },
        { name: text.history, href: '/history', icon: History },
        { name: text.stats, href: '/stats', icon: BookMarked },
      ]
    : [];

  const adminNav = [
    { name: text.adminPanel, href: '/admin', icon: Settings },
    ...(canAccessQuestionManagement(user?.role) ? [{ name: text.questions, href: '/admin/questions', icon: Database }] : []),
    ...(canAccessQuestionApproval(user?.role) ? [{ name: text.approvals, href: '/admin/question-approvals', icon: ClipboardCheck }] : []),
    ...(canAccessMediaManagement(user?.role) ? [{ name: text.media, href: '/admin/media', icon: Video }] : []),
    ...(canAccessVocabManagement(user?.role) ? [{ name: text.vocab, href: '/admin/vocab', icon: Library }] : []),
  ];

  const bottomNav = canAccessAdmin
    ? [...primaryNav, { name: text.admin, href: '/admin', icon: Settings }]
    : [...primaryNav];

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div
      className="min-h-screen bg-[#EEF2FF] font-sans"
      style={{
        paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      {/* Compact Top Bar */}
      <nav
        className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-indigo-100 px-4 py-2.5 flex items-center justify-between"
        style={{
          paddingTop: 'calc(0.625rem + env(safe-area-inset-top, 0px))',
        }}
      >
        <Link to={homeHref} className="flex items-center gap-2 active:opacity-70">
          <div className="w-9 h-9 bg-[#4F46E5] rounded-xl flex items-center justify-center text-white font-bold text-base">
            HM
          </div>
          <span className="font-bold text-base text-[#312E81]">Huanle</span>
        </Link>

        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <div
              className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded-lg text-[11px] font-semibold"
              title={`${pendingCount} 次提交待上传`}
            >
              <CloudOff className="w-3 h-3" />
              <span>{pendingCount}</span>
            </div>
          )}
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 active:bg-gray-100"
            aria-label={text.openMenu}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="px-4 pt-4">
        <Outlet />
      </main>

      {/* Bottom Tab Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex justify-around items-stretch">
          {bottomNav.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.href === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${
                  isActive ? 'text-[#4F46E5]' : 'text-gray-400 active:text-gray-600'
                }`}
              >
                <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : ''}`} />
                <span className={`text-[11px] ${isActive ? 'font-bold' : 'font-medium'}`}>
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Slide-in Drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in"
          onClick={closeDrawer}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right"
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-[#4F46E5] font-bold">
                  {user?.username?.charAt(0).toUpperCase() ?? '?'}
                </div>
                <div>
                  <div className="font-bold text-[#312E81] text-sm">{user?.username}</div>
                  <div className="text-xs text-gray-500 capitalize">{normalizeUserRole(user?.role)}</div>
                </div>
              </div>
              <button
                onClick={closeDrawer}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 active:bg-gray-100"
                aria-label={text.closeMenu}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {primaryNav.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeDrawer}
                    className={`flex items-center gap-3 px-4 py-3 ${
                      isActive ? 'text-[#4F46E5] bg-indigo-50' : 'text-gray-600 active:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-semibold">{item.name}</span>
                  </Link>
                );
              })}

              {canAccessAdmin && (
                <>
                  <div className="px-4 mt-4 mb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    {text.adminSection}
                  </div>
                  {adminNav.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeDrawer}
                        className={`flex items-center gap-3 px-4 py-3 ${
                          isActive ? 'text-[#4F46E5] bg-indigo-50' : 'text-gray-600 active:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-semibold">{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              )}
            </div>

            <div className="border-t border-gray-100 p-3">
              <button
                onClick={() => {
                  closeDrawer();
                  void performHardLogout({ redirectToLogin: true });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 active:bg-red-50 font-semibold"
              >
                <LogOut className="w-5 h-5" />
                {text.logout}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutMobile;
